<template>
    <section class="o-flexible-module" :class="{ 'o-flexible-module--flush': settings.spacing == 'flush' }">
        <div class="grid-container" v-if="settings.showComponentTitle">
            <div class="grid-x grid-margin-x">
                <div class="small-12 cell">
                    <div class="utl-heading-beta">{{ settings.componentTitle || 'Accordions' }}</div>
                </div>
            </div>
        </div>

        <div
            class="c-accordions"
            :class="[
                `c-accordions--${settings.theme}`,
                {
                    'c-accordions--secondary': displayType == 'secondary',
                },
            ]"
            v-if="displayType == 'secondary'">
            <div class="grid-container">
                <div class="grid-x grid-margin-x">
                    <div class="small-12 medium-6 cell">
                        <div data-aos="fade-up">
                            <h2 class="c-accordions__title">
                                {{ title }}
                            </h2>

                            <div class="c-accordions__content s-wp-cms" v-html="content" v-if="content"></div>

                            <PartialsCtaButton :link="cta" :theme="settings.theme" />
                        </div>

                        <div :class="`c-accordions__accordions has-${listFormat}-list`">
                            <div
                                v-for="(accordion, index) in accordions"
                                :key="index"
                                class="c-accordion"
                                :class="{ 'is-open': index === activeItem }">
                                <button
                                    class="c-accordion__title"
                                    type="button"
                                    :aria-label="`Toggle '${accordion.title}'`"
                                    @click="openAccordion(index)">
                                    <img
                                        v-if="
                                            listFormat == 'icons' &&
                                            accordion.icon &&
                                            !accordion.icon.mimeType.includes('svg')
                                        "
                                        class="c-accordion__title-icon"
                                        :src="accordion.icon.sourceUrl"
                                        :alt="accordion.icon.altText"
                                        :title="accordion.icon.title" />

                                    <inline-svg
                                        v-if="
                                            listFormat == 'icons' &&
                                            accordion.icon &&
                                            accordion.icon.mimeType.includes('svg')
                                        "
                                        class="c-accordion__title-icon"
                                        :src="accordion.icon.sourceUrl"
                                        :alt="accordion.icon.altText"
                                        :title="accordion.icon.title"></inline-svg>

                                    <span>{{ accordion.title }}</span>
                                    <SvgoIconChevronDown class="c-accordion__title-arrow" />
                                </button>

                                <div class="c-accordion__content s-wp-cms" v-html="accordion.content"></div>

                                <PartialsCtaButton class="c-accordion__cta" :link="accordion.cta" :theme="settings.theme" />
                            </div>
                        </div>
                    </div>

                    <div class="small-12 medium-5 medium-offset-1 cell" v-if="image">
                        <div class="c-accordions__media" :class="[{ 'js-parallax__wrapper': !isMobile }]">
                            <img
                                :class="[{ 'js-parallax': !isMobile }]"
                                :src="image.sourceUrl"
                                :alt="image.altText"
                                :title="image.title"
                                :sizes="image.sizes"
                                :srcset="image.srcSet" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div :class="`c-accordions c-accordions--${settings.theme}`" v-else>
            <div class="grid-container">
                <div class="grid-x grid-margin-x">
                    <div class="small-12 medium-12 large-5 cell">
                        <h2 class="c-accordions__title" data-aos="fade-up">
                            {{ title }}
                        </h2>
                    </div>
                </div>
            </div>

            <div class="grid-container">
                <div class="grid-x grid-margin-x">
                    <div class="small-12 medium-12 large-5 cell">
                        <div
                            class="c-accordions__content s-wp-cms"
                            data-aos="fade-up"
                            v-if="content"
                            v-html="content"></div>

                        <PartialsCtaButton :link="cta" :theme="settings.theme" />
                    </div>

                    <div class="small-12 medium-10 medium-offset-2 large-6 large-offset-1 cell">
                        <div :class="`c-accordions__accordions has-${listFormat}-list`">
                            <div
                                v-for="(accordion, index) in accordions"
                                :key="index"
                                class="c-accordion"
                                :class="{ 'is-open': index === activeItem }">
                                <div class="c-accordion__title" v-on:click="openAccordion(index)">
                                    <img
                                        v-if="
                                            listFormat == 'icons' &&
                                            accordion.icon &&
                                            !accordion.icon.mimeType.includes('svg')
                                        "
                                        class="c-accordion__title-icon"
                                        :src="accordion.icon.sourceUrl"
                                        :alt="accordion.icon.altText"
                                        :title="accordion.icon.title" />

                                    <inline-svg
                                        v-if="
                                            listFormat == 'icons' &&
                                            accordion.icon &&
                                            accordion.icon.mimeType.includes('svg')
                                        "
                                        class="c-accordion__title-icon"
                                        :src="accordion.icon.sourceUrl"
                                        :alt="accordion.icon.altText"
                                        :title="accordion.icon.title"></inline-svg>

                                    {{ accordion.title }}
                                    <SvgoIconChevronDown class="c-accordion__title-arrow" />
                                </div>

                                <div class="c-accordion__content s-wp-cms" v-html="accordion.content"></div>

                                <PartialsCtaButton class="c-accordion__cta" :link="accordion.cta" :theme="settings.theme" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
    import InlineSvg from 'vue-inline-svg'
    import { useMediaQuery } from '@vueuse/core'

    const props = defineProps({
        title: {
            type: String,
            required: true,
        },
        content: {
            type: String,
            required: false,
        },
        cta: {
            type: Object,
            required: false,
        },
        accordions: {
            type: Object,
            required: true,
        },
        image: {
            type: Object,
            required: false,
        },
        displayType: {
            type: String,
            required: true,
        },
        listFormat: {
            type: String,
            required: true,
        },
        settings: {
            theme: {
                type: String,
                required: true,
            },
            showComponentTitle: {
                type: Boolean,
                required: true,
            },
            spacing: {
                type: String,
                required: true,
            },
        },
    })

    const isMobile = useMediaQuery('(max-width: 767px)')
    const activeItem = ref(null)

    const openAccordion = (item) => {
        if (activeItem.value == item) {
            activeItem.value = null
        } else {
            activeItem.value = item
        }
    }

    onMounted(() => {
        if (isMobile.value == false) {
            const $image = document.getElementsByClassName('js-parallax')
            new simpleParallax($image, { delay: 0.6, transition: 'cubic-bezier(0,0,0,1)' })
        }
    })
</script>
